import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ConditionalElement } from "../../../../../rule-engine-core/TextMerge/Elements/ConditionalElement";
import { FieldElement } from "../../../../../rule-engine-core/TextMerge/Elements/Text/FieldElement";
import { TextElement } from "../../../../../rule-engine-core/TextMerge/Elements/Text/TextElement";
import { TextMergeConfiguration } from "../../../../../rule-engine-core/TextMerge/TextMergeConfiguration";
import { TextMergeType } from "../../../../../rule-engine-core/TextMerge/TextMergeType";
import { createRuleSummaryPresentation } from "../../ruleIntegrationTypes";
import {
    runTextMergeToPlainText,
    type CreateTextMergeIntegrationAdapterParams,
} from "../../executableFamilies/textMerge/TextMergeIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { DemoData, EnrichedPart } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import {
    buildCrmProductName,
    buildTransformationPreview,
    compactMpn,
    formatCapacitance,
    formatResistance,
    formatTolerance,
    formatVoltage,
} from "../../../outputs/buildDownstreamPayloads";
import type { TextMergePartDemoRow } from "./tr006ErpDescriptionTextMergePolicy";

export const TR007_RULE_ID = "TR-007";
export const TR007_APPGLUE_EXECUTABLE_TYPE = "TextMergeConfiguration";
export const TR007_OUTPUT_QUEUE: DemoOutputQueue = "salesforce_product_name_previews";

export type Tr007TextMergeEvidence = {
    status: DemoRuleStatus;
    textMergeOutput: string;
    expectedSeededProductName: string;
    previewStatus: string;
    outputQueue?: DemoOutputQueue;
};

const TEXT_FIELD_NAMES = [
    "manufacturer_name",
    "mpn",
    "component_value",
    "voltage_range",
    "package_type",
    "tolerance",
] as const;

const BOOLEAN_FIELD_NAMES = [
    "has_component_value",
    "has_voltage_range",
    "has_package_type",
    "has_tolerance",
] as const;

type TextMergeProductNameField = (typeof TEXT_FIELD_NAMES)[number];
type TextMergeProductNameCondition = (typeof BOOLEAN_FIELD_NAMES)[number];

const createTextMergeProductNameInputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    TEXT_FIELD_NAMES.forEach((fieldName) => inputData.fields.push(new StringDataDefinition(fieldName)));
    BOOLEAN_FIELD_NAMES.forEach((fieldName) => inputData.fields.push(new BooleanDataDefinition(fieldName)));
    return inputData;
};

const textField = (label: string, variableName: TextMergeProductNameField): FieldElement => {
    const element = new FieldElement(label);
    element.fieldData.variableName = variableName;
    return element;
};

const addField = (config: TextMergeConfiguration, label: string, variableName: TextMergeProductNameField) => {
    config.rootElement.addElement(textField(label, variableName));
};

const addConditionalField = (
    config: TextMergeConfiguration,
    conditionField: TextMergeProductNameCondition,
    label: string,
    variableName: TextMergeProductNameField,
    prefix?: string,
) => {
    const conditional = new ConditionalElement();
    conditional.condition.variableName = conditionField;
    if (prefix) conditional.addElement(new TextElement(prefix));
    conditional.addElement(textField(label, variableName));
    config.rootElement.addElement(conditional);
};

export const createTr007CrmProductNameTextMergeConfiguration = (): TextMergeConfiguration => {
    AppGlueCoreInit.init();

    const config = new TextMergeConfiguration();
    config.type = TextMergeType.PlAIN;
    config.defaultValue = "";
    config.inputData = createTextMergeProductNameInputDefinition();
    config.executionResult = new StringDataDefinition();

    addField(config, "Manufacturer", "manufacturer_name");
    addField(config, "MPN", "mpn");
    addConditionalField(config, "has_component_value", "Component value", "component_value", "-");
    addConditionalField(config, "has_voltage_range", "Voltage range", "voltage_range");
    addConditionalField(config, "has_package_type", "Package", "package_type");
    addConditionalField(config, "has_tolerance", "Tolerance", "tolerance");

    return config;
};

const present = (value: string | null | undefined): value is string => typeof value === "string" && value.length > 0;

const buildTextMergeProductNameFields = (part: EnrichedPart): Record<string, string | boolean> => {
    const componentValue = formatCapacitance(part.capacitancePf) ?? formatResistance(part.resistanceOhms);
    const voltageRange = formatVoltage(part);
    const packageType = part.packageType ?? "";
    const tolerance = formatTolerance(part.tolerancePct);

    return {
        manufacturer_name: part.manufacturerName,
        mpn: compactMpn(part.mpn),
        component_value: componentValue ?? "",
        voltage_range: voltageRange ?? "",
        package_type: packageType,
        tolerance: tolerance ?? "",
        has_component_value: present(componentValue),
        has_voltage_range: present(voltageRange),
        has_package_type: present(packageType),
        has_tolerance: present(tolerance),
    };
};

export const buildTr007ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData(buildTextMergeProductNameFields(part));

const evaluateTr007ProductName = (
    config: TextMergeConfiguration,
    data: DemoData,
    part: EnrichedPart,
): Tr007TextMergeEvidence => {
    const textMergeOutput = runTextMergeToPlainText(config, buildTr007ExecutionData(part));
    const preview = buildTransformationPreview(data, part);
    const status: DemoRuleStatus = part.hasMpnFormatIssue ? "flagged" : "pass";

    return {
        status,
        textMergeOutput,
        expectedSeededProductName: buildCrmProductName(part),
        previewStatus: preview.status,
        outputQueue: status === "pass" ? TR007_OUTPUT_QUEUE : "pending_review",
    };
};

const renderConfiguredFields = (config: TextMergeConfiguration): string => {
    const fields = config.rootElement.elements
        .flatMap((element) => ("elements" in element ? element.elements : [element]))
        .filter((element): element is FieldElement => element instanceof FieldElement)
        .map((element) => element.fieldName);

    return fields.length > 0 ? fields.join(", ") : "fields stored in TextMergeConfiguration";
};

const renderTr007Summary = (config: TextMergeConfiguration) =>
    createRuleSummaryPresentation({
        status: "complete",
        lines: [
            `TEXT MERGE ${renderConfiguredFields(config)}`,
            "VALIDATE MPN is not a placeholder before CRM publish",
            "ACTION generate CRM product name or hold suspicious MPNs",
        ],
        sources: ["native_appglue_config", "adapter_projection"],
    });

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-TR007-SAMPLE",
    mpn: "VJ0402Y104KXACW1BC",
    manufacturerId: "MFR-004",
    manufacturerName: "Vishay",
    category: "Capacitor",
    taxonomyPath: "Passive > Capacitor > Ceramic",
    packageType: "0402",
    pinCount: null,
    voltageMin: 0,
    voltageMax: 16,
    capacitancePf: 100000,
    resistanceOhms: null,
    tolerancePct: 10,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 95,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

const sampleData = (part: EnrichedPart): DemoData =>
    (({
        seed: 20260501,
        manufacturers: [],
        parts: [part],
        catalog: [],
        priceUpdates: [],
        pricingHistory: [],
        priceToleranceRules: [],
        taxonomyMappings: [
            {
                taxonomyPath: part.taxonomyPath,
                erpProductGroup: "PAS-CAP",
                erpProductLine: "Capacitors",
                crmFamily: "Passives",
            },
        ],
        sourcePriorityRules: [],
        opportunities: [],
        purchaseOrders: [],
        summary: {},
    }) as unknown) as DemoData;

export const TR007_TEXT_MERGE_POLICY: CreateTextMergeIntegrationAdapterParams<TextMergePartDemoRow> = {
    ruleId: TR007_RULE_ID,
    policyBinding: requireRulePolicyBinding(TR007_RULE_ID),
    createConfig: createTr007CrmProductNameTextMergeConfiguration,
    evaluator: {
        evaluate: (config, row) => {
            const evidence = evaluateTr007ProductName(config, row.data, row.part);

            return {
                passed: evidence.status === "pass" && evidence.previewStatus === "ready",
                status: evidence.status,
                reasonCode: evidence.status === "pass" ? "PRODUCT_NAME_READY" : "PRODUCT_NAME_INPUT_SUSPECT",
                explanation:
                    evidence.status === "pass"
                        ? "CRM product-name preview was built from the saved AppGlue text merge."
                        : "CRM product name is held because the MPN looks like a placeholder.",
                outputQueue: evidence.outputQueue,
                comparisons: [
                    {
                        label: "CRM product name",
                        expected: "readable text merge",
                        actual: evidence.textMergeOutput || "empty",
                        ok: evidence.previewStatus === "ready" && evidence.textMergeOutput.length > 0,
                    },
                    {
                        label: "Seeded template parity",
                        expected: evidence.expectedSeededProductName,
                        actual: evidence.textMergeOutput || "empty",
                        ok: evidence.textMergeOutput === evidence.expectedSeededProductName,
                    },
                ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: TR007_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: {
        renderSummary: renderTr007Summary,
    },
};

const passingPart = samplePart();
const routedPart = samplePart({
    partId: "PART-TR007-ROUTED",
    mpn: "TBD",
    hasMpnFormatIssue: true,
});

export const TR007_RUNTIME_SAMPLES = {
    passingRow: { data: sampleData(passingPart), part: passingPart },
    routedRow: { data: sampleData(routedPart), part: routedPart },
};
